import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users, TrendingUp } from "lucide-react";
import { api, errText, fmtAmount, fmtDate, fmtUSD, CURRENCIES } from "@/lib/apiClient";
import { useDicts } from "@/hooks/useDicts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import DictSelect from "@/components/DictSelect";

const RECURRENCE = { once: "Разово", weekly: "Каждую неделю", monthly: "Каждый месяц", quarterly: "Каждый квартал" };
const ALL = "all";

const empty = () => ({
  name: "", company_id: "", kind: "client", recurrence: "monthly", amount: "",
  currency: "USD", start_date: new Date().toISOString().slice(0, 10),
  project_id: "", category_id: "", contact: "", active: true, generate_operations: true,
});

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(ALL);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const { dicts, reload } = useDicts();

  const load = useCallback(async () => {
    const { data } = await api.get("/clients", {
      params: companyFilter !== ALL ? { company_id: companyFilter } : {},
    });
    setClients(data);
  }, [companyFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.name || !form.company_id || !form.amount) {
      toast.error("Заполните имя, компанию и сумму");
      return;
    }
    const payload = {
      ...form,
      amount: Number(form.amount),
      project_id: form.project_id || null,
      category_id: form.category_id || null,
    };
    try {
      if (editId) await api.put(`/clients/${editId}`, payload);
      else {
        const { data } = await api.post("/clients", payload);
        toast.success(`Клиент добавлен, создано операций: ${data.generated_operations}`);
      }
      setOpen(false);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    await api.delete(`/clients/${c.id}`, { params: { delete_operations: true } });
    toast.success("Клиент удалён");
    load();
  };

  const totalReceived = clients.reduce((a, c) => a + (c.received_usd || 0), 0);
  const totalPlanned = clients.reduce((a, c) => a + (c.planned_usd || 0), 0);

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Клиенты и инвесторы</h1>
          <p className="mt-1 text-sm text-muted-foreground">Поступления автоматически фиксируются как доход в P&amp;L</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-[220px]" data-testid="clients-company-filter">
              <SelectValue placeholder="Компания" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все компании</SelectItem>
              {dicts.companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="gap-2" data-testid="add-client-button" onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }}>
            <Plus className="h-4 w-4" /> Клиент
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          ["Всего клиентов", clients.length, Users, "clients-count"],
          ["Получено", fmtUSD(totalReceived), TrendingUp, "clients-received"],
          ["Ожидается", fmtUSD(totalPlanned), TrendingUp, "clients-planned"],
        ].map(([label, value, Icon, tid]) => (
          <Card className="glass metric-card" key={tid} data-testid={`metric-${tid}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-2 font-mono text-2xl font-bold">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border" data-testid="clients-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Название</TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>Компания</TableHead>
              <TableHead>Периодичность</TableHead>
              <TableHead className="text-right">Сумма</TableHead>
              <TableHead className="text-right">Получено USD</TableHead>
              <TableHead className="text-right">Ожидается USD</TableHead>
              <TableHead>Старт</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="row-anim">
                <TableCell className="font-medium">
                  {c.name}
                  {c.contact && <div className="text-xs text-muted-foreground">{c.contact}</div>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={c.kind === "investor" ? "border-transfer/30 bg-transfer/15 text-transfer" : "border-primary/30 bg-primary/15 text-primary"}>
                    {c.kind === "investor" ? "Инвестор" : "Клиент"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.company_name}</TableCell>
                <TableCell className="text-sm">{RECURRENCE[c.recurrence]}</TableCell>
                <TableCell className="whitespace-nowrap text-right font-mono text-sm">{fmtAmount(c.amount, c.currency)}</TableCell>
                <TableCell className="text-right font-mono text-sm text-income">{fmtUSD(c.received_usd)}</TableCell>
                <TableCell className="text-right font-mono text-sm text-warning">{fmtUSD(c.planned_usd)}</TableCell>
                <TableCell className="font-mono text-xs">{fmtDate(c.start_date)}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-client-${c.id}`}
                      onClick={() => { setForm({ ...empty(), ...c }); setEditId(c.id); setOpen(true); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-client-${c.id}`} onClick={() => remove(c)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!clients.length && (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground" data-testid="clients-empty">
                  Клиентов пока нет
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editId ? "Редактировать клиента" : "Новый клиент / инвестор"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Название</Label>
              <Input value={form.name} data-testid="client-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Компания</Label>
              <DictSelect kind="companies" testid="client-company" items={dicts.companies} value={form.company_id}
                onChange={(v) => setForm({ ...form, company_id: v })} onCreated={reload} placeholder="Компания" />
            </div>
            <div className="space-y-2">
              <Label>Тип</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="client-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Клиент</SelectItem>
                  <SelectItem value="investor">Инвестор</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Периодичность</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                <SelectTrigger data-testid="client-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RECURRENCE).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Дата начала</Label>
              <Input type="date" value={form.start_date} data-testid="client-start-date"
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Сумма</Label>
              <Input type="number" value={form.amount} data-testid="client-amount"
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Валюта</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="client-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Проект</Label>
              <DictSelect kind="projects" testid="client-project" items={dicts.projects} value={form.project_id}
                onChange={(v) => setForm({ ...form, project_id: v })} onCreated={reload} placeholder="Проект" />
            </div>
            <div className="space-y-2">
              <Label>Категория дохода</Label>
              <DictSelect kind="categories" testid="client-category" items={dicts.categories} value={form.category_id}
                onChange={(v) => setForm({ ...form, category_id: v })} onCreated={reload} placeholder="Категория" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Контакт</Label>
              <Input value={form.contact || ""} data-testid="client-contact" placeholder="email или телефон"
                onChange={(e) => setForm({ ...form, contact: e.target.value })} />
            </div>
            {!editId && (
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch checked={form.generate_operations} data-testid="client-generate-ops"
                  onCheckedChange={(v) => setForm({ ...form, generate_operations: v })} />
                <span className="text-sm text-muted-foreground">Создать операции дохода в P&amp;L автоматически</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="client-cancel">Отмена</Button>
            <Button onClick={save} data-testid="client-save">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
